import { Injectable, signal } from '@angular/core';

const TOKEN_KEY = 'access_token';
const EXPIRES_AT_KEY = 'access_token_expires_at';

/**
 * Owns the access token and its expiry.
 *
 * Deliberately dependency-free: the HTTP interceptors inject it, and injecting
 * AuthStore there instead would close a cycle
 * (store -> api -> HttpClient -> interceptor -> store). `apiErrorInterceptor`
 * does need the store to end an expired session, and resolves it lazily through
 * `Injector` on the 401 path alone rather than eagerly on every request.
 *
 * Persisted in sessionStorage because `/connect/token` issues no refresh token
 * — the ~1h access token is the whole session, and sessionStorage keeps it
 * across a page reload while dropping it when the tab closes.
 */
@Injectable({ providedIn: 'root' })
export class SessionToken {
  private readonly accessToken = signal(sessionStorage.getItem(TOKEN_KEY));
  private readonly expiresAt = signal(Number(sessionStorage.getItem(EXPIRES_AT_KEY)) || 0);

  readonly value = this.accessToken.asReadonly();

  /**
   * A method rather than a computed: expiry depends on wall-clock time, which
   * is not a signal, so a computed would serve a stale cached answer until the
   * token itself changed.
   */
  hasValidToken(): boolean {
    return this.accessToken() !== null && Date.now() < this.expiresAt();
  }

  isTokenExpired(): boolean {
    return this.accessToken() !== null && Date.now() >= this.expiresAt();
  }

  set(accessToken: string, expiresInSeconds: number): void {
    const expiresAt = Date.now() + expiresInSeconds * 1000;
    sessionStorage.setItem(TOKEN_KEY, accessToken);
    sessionStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
    this.accessToken.set(accessToken);
    this.expiresAt.set(expiresAt);
  }

  clear(): void {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EXPIRES_AT_KEY);
    this.accessToken.set(null);
    this.expiresAt.set(0);
  }
}
